import React, { useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import { useFetching } from '@mssgme/hooks';
import { ImageFieldProps } from './types';
import { ImageUpload } from '../Inputs';
import FieldRow from './FieldRow';

export const useUploadCallback = ({ name, upload, onChange }) => {
    return useFetching(async (e, uploaded) => {
        const file = get(e, 'target.files.0');

        if (file) {
            onChange(await upload({ payload: file, field: name }));
        } else if (uploaded) {
            onChange(uploaded);
        }
    });
};

export default function ImageField({ as: Component, upload, name, previewName, onChange, children, ...rest }) {
    const { setFieldError, setFieldValue, setFieldTouched, getFieldMeta } = useFormikContext();
    const { error: formError } = getFieldMeta(name);

    const { run: handleChange, pending: isFetching, getValidationError } = useUploadCallback({
        name,
        upload,
        onChange: useCallback(
            (upload) => {
                if (onChange) {
                    onChange(upload);
                } else {
                    setFieldValue(name, upload.filename || '');
                    previewName && setFieldValue(previewName, upload.url || '');
                    setFieldTouched(name, true, false);
                }
            },
            [name, previewName, onChange]
        ),
    });
    const error = getValidationError(name) || formError;

    useEffect(() => {
        if (error && error !== formError) {
            setFieldError(name, error);
            setFieldTouched(name, true, false);
        }
    }, [error, formError]);

    return (
        <FieldRow name={name} {...rest}>
            {({ field }) => (
                <Component
                    {...field}
                    isUploading={isFetching}
                    preview={typeof children === 'function' ? children({ isFetching }) : children}
                    onChange={handleChange}
                />
            )}
        </FieldRow>
    );
}

ImageField.propTypes = ImageFieldProps;

ImageField.defaultProps = {
    as: ImageUpload,
};
